import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CabinetLetterheadListResponse,
  CabinetLetterheadPatch,
  CabinetLetterheadResponse,
} from '@hector/shared-types';
import { ErrorService, type ApiError } from '@/lib/errors';
import {
  createCabinetLetterhead,
  deleteCabinetLetterhead,
  fetchCabinetLetterheadImageBlobById,
  fetchCabinetLetterheads,
  updateCabinetLetterhead,
  uploadCabinetLetterheadDocument,
} from '@/services/actPersonalizationApi';
import type { EnTeteAPoser } from '@/features/redaction/editor/canvasLetterhead';

/**
 * Collection des en-têtes NOMMÉS du cabinet (migration 163).
 *
 * Clé DISTINCTE de `cabinet-letterhead` (au singulier, {@link useLetterhead}), qui sert l'en-tête
 * PAR DÉFAUT aux écrans de rendu. Les deux se recoupent — promouvoir ou supprimer un en-tête
 * change ce que le singulier renvoie — d'où l'invalidation croisée ci-dessous.
 */
const QUERY_KEY = 'cabinet-letterheads';

/** Clé de l'en-tête par défaut, invalidée par tout geste qui peut changer QUI est le défaut. */
const DEFAULT_LETTERHEAD_QUERY_KEY = 'cabinet-letterhead';

/**
 * Ces mutations écrivent une configuration que l'avocat ne revérifie pas : un échec muet
 * lui laisse croire que son en-tête est enregistré, et il ne le découvre qu'en générant un acte.
 * Chacune remonte donc l'échec — même règle que le flux Tampon et que {@link useLetterhead}.
 */
function signalerLEchec(error: unknown, fallback: string, userMessage: string, action: string) {
  ErrorService.handle(error instanceof Error ? error : new Error(fallback), {
    userMessage,
    context: { action },
  });
}

/**
 * Le serveur a-t-il REFUSÉ le dépôt (400 ou 422), plutôt qu'échoué à le traiter ?
 *
 * Un refus porte la raison qui permet à l'avocat de corriger : document sans en-tête ni pied de
 * page, fichier qui n'est pas un Word valide, nom déjà pris. Une phrase générique à sa place le ferait
 * redéposer le même fichier.
 */
function raisonDuRefus(error: unknown): string | null {
  if (!(error instanceof Error) || error.message.length === 0) return null;
  const { statusCode } = error as ApiError;
  return statusCode === 400 || statusCode === 422 ? error.message : null;
}

/** Tous les en-têtes du cabinet, défaut en tête. */
export function useLetterheads(cabinetId: string | null) {
  return useQuery({
    queryKey: [QUERY_KEY, cabinetId],
    enabled: !!cabinetId,
    queryFn: (): Promise<CabinetLetterheadListResponse> => fetchCabinetLetterheads(cabinetId as string),
  });
}

/**
 * Invalide la collection ET l'en-tête par défaut.
 *
 * Ces gestes peuvent déplacer le défaut : la promotion évidemment, mais aussi la
 * suppression (le serveur promeut un successeur) et l'ajout (le premier en-tête d'un cabinet
 * devient le défaut). Ne rafraîchir que la liste laisserait l'éditeur d'apparence, juste en
 * dessous, régler l'ancien défaut — sur un écran où les deux sont visibles en même temps.
 *
 * Le dépôt et le retrait d'un PAPIER y passent aussi : la clé au singulier porte `hasDocument`,
 * que le canvas lit pour savoir sur quoi ouvrir un nouveau document.
 */
function useInvalidationDesEnTetes(cabinetId: string | null) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY, cabinetId] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    queryClient.invalidateQueries({ queryKey: [DEFAULT_LETTERHEAD_QUERY_KEY] });
  };
}

/** Dépose un nouvel en-tête nommé (papier Word ou logo, nom, note d'usage). */
export function useCreateLetterhead(cabinetId: string | null) {
  const onSuccess = useInvalidationDesEnTetes(cabinetId);
  return useMutation({
    mutationFn: (input: { file: File; name: string; usageNote?: string }): Promise<CabinetLetterheadResponse> => {
      if (!cabinetId) throw new Error('useCreateLetterhead: cabinetId is null — cannot create letterhead');
      return createCabinetLetterhead(cabinetId, input);
    },
    onSuccess,
    onError: (error, variables) => {
      const raison = raisonDuRefus(error);
      signalerLEchec(
        error,
        `Impossible de déposer l'en-tête « ${variables.name} »`,
        raison !== null
          ? `L'en-tête « ${variables.name} » a été refusé : ${raison}`
          : `L'en-tête « ${variables.name} » n'a pas pu être déposé. Vérifiez votre connexion et réessayez.`,
        'create_letterhead',
      );
    },
  });
}

/** Renomme, réécrit la note d'usage, ou promeut en défaut (`isDefault: true`). */
export function useUpdateLetterhead(cabinetId: string | null) {
  const onSuccess = useInvalidationDesEnTetes(cabinetId);
  return useMutation({
    mutationFn: (input: { letterheadId: string; patch: CabinetLetterheadPatch }): Promise<CabinetLetterheadResponse> => {
      if (!cabinetId) throw new Error('useUpdateLetterhead: cabinetId is null — cannot update letterhead');
      return updateCabinetLetterhead(cabinetId, input.letterheadId, input.patch);
    },
    onSuccess,
    onError: (error) =>
      signalerLEchec(
        error,
        "Impossible de modifier l'en-tête",
        "L'en-tête n'a pas pu être modifié. Vérifiez votre connexion et réessayez.",
        'update_letterhead',
      ),
  });
}

/** Supprime l'en-tête et ses fichiers ; le serveur promeut un successeur si c'était le défaut. */
export function useDeleteLetterhead(cabinetId: string | null) {
  const onSuccess = useInvalidationDesEnTetes(cabinetId);
  return useMutation({
    mutationFn: (letterheadId: string) => {
      if (!cabinetId) throw new Error('useDeleteLetterhead: cabinetId is null — cannot delete letterhead');
      return deleteCabinetLetterhead(cabinetId, letterheadId);
    },
    onSuccess,
    onError: (error) =>
      signalerLEchec(
        error,
        "Impossible de supprimer l'en-tête",
        "L'en-tête n'a pas pu être supprimé. Réessayez dans un instant.",
        'delete_letterhead',
      ),
  });
}
/**
 * Dépose le papier (Word) d'un en-tête nommé, ou remplace celui qu'il porte.
 *
 * `letterheadName` ne part pas au serveur : il sert à NOMMER l'en-tête dans le message d'échec,
 * un cabinet pouvant en avoir vingt.
 */
export function useUploadLetterheadDocument(cabinetId: string | null) {
  const onSuccess = useInvalidationDesEnTetes(cabinetId);
  return useMutation({
    mutationFn: (input: {
      letterheadId: string;
      letterheadName: string;
      file: File;
    }): Promise<CabinetLetterheadResponse> => {
      if (!cabinetId) {
        throw new Error('useUploadLetterheadDocument: cabinetId is null — cannot upload letterhead document');
      }
      return uploadCabinetLetterheadDocument(cabinetId, input.letterheadId, input.file);
    },
    onSuccess,
    onError: (error, variables) => {
      const raison = raisonDuRefus(error);
      signalerLEchec(
        error,
        `Impossible de déposer le papier de l'en-tête « ${variables.letterheadName} »`,
        raison !== null
          ? `Le papier à en-tête de « ${variables.letterheadName} » a été refusé : ${raison}`
          : `Le papier à en-tête de « ${variables.letterheadName} » n'a pas pu être déposé. Vérifiez votre connexion et réessayez.`,
        'upload_letterhead_document',
      );
    },
  });
}

/**
 * Relit un en-tête NOMMÉ — réglages ET octets — à la demande, hors de tout état de rendu.
 *
 * Pendant nommé de `useFetchLetterheadFrais` (qui sert l'en-tête par DÉFAUT). Même doctrine :
 * `fetchQuery` et non `refetch`, `staleTime: 0`, et un REJET si le réseau échoue — « je ne sais
 * pas » et « cet en-tête n'a pas d'image » n'envoient pas l'avocat au même endroit.
 *
 * Les réglages viennent de la LISTE, relue au même instant : un en-tête renommé, désactivé ou
 * supprimé dans un autre onglet ne doit pas être posé sur la foi d'un cache.
 */
export function useFetchEnTeteNommeFrais(
  cabinetId: string | null,
): (letterheadId: string) => Promise<EnTeteAPoser> {
  const queryClient = useQueryClient();
  return async (letterheadId: string): Promise<EnTeteAPoser> => {
    if (!cabinetId) {
      throw new Error(
        "useFetchEnTeteNommeFrais : aucun cabinet actif — impossible de relire l'en-tete. Attendre que `useCabinetId()` soit resolu avant d'appeler.",
      );
    }
    const liste = await queryClient.fetchQuery({
      queryKey: [QUERY_KEY, cabinetId],
      queryFn: (): Promise<CabinetLetterheadListResponse> => fetchCabinetLetterheads(cabinetId),
      staleTime: 0,
    });
    const cible = liste.letterheads.find((l) => l.id === letterheadId);
    if (!cible) {
      throw new Error(
        `useFetchEnTeteNommeFrais : l'en-tete ${letterheadId} n'existe plus dans le cabinet ${cabinetId} — il a pu etre supprime depuis l'ouverture du menu.`,
      );
    }

    const settings = {
      enabled: cible.enabled,
      // `filePath`/`mimeType` sont ABSENTS de la forme client (cle S3 privee) et la pose ne les
      // lit pas : seule la largeur voulue compte ici, les octets arrivent separement.
      image: cible.hasImage
        ? { ...(cible.imageWidthMm !== null ? { widthMm: cible.imageWidthMm } : {}) }
        : null,
      alignment: cible.alignment,
    };

    // Pas de fichier ⇒ rien a telecharger, et l'appelant a deja tout pour le dire a l'avocat.
    if (!cible.enabled || !cible.hasImage) return { settings, imageBlob: null };

    const imageBlob = await queryClient.fetchQuery({
      queryKey: [QUERY_KEY, cabinetId, letterheadId, 'image-blob'],
      queryFn: (): Promise<Blob> => fetchCabinetLetterheadImageBlobById(cabinetId, letterheadId),
      staleTime: 0,
    });
    return { settings, imageBlob };
  };
}
